import React, { FC, useEffect, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { usePlayerRouter } from 'hooks/usePlayerRouter';
import { PlayerProps, PlayingAssetType } from 'types/Player';
import LivePlayer from 'components/Pages/LivePlayer/LivePlayer';
import CatchupPlayer from 'components/Pages/CatchupPlayer/CatchupPlayer';
import { decodeId } from 'utils/fnUrl';
import RecordingPlayer from 'components/Pages/RecordingPlayer/RecordingPlayer';
import VodPlayer from 'components/Pages/VodPlayer/VodPlayer';
import TrailerPlayer from 'components/Pages/TrailerPlayer/TrailerPlayer';
import { usePlayer } from 'providers/player/PlayerContext';
import { useNavigation } from 'hooks/useNavigation';
import { useConfig } from 'providers/useConfig/ConfigContext';
import { useCast } from 'providers/cast/CastContext';

export const Player: FC<{ props?: PlayerProps; isEmbedded?: boolean }> = ({ props: inputProps, isEmbedded = false }) => {
    const {
        miniPlayerProps,
        switchToMiniPlayer,
        closeMiniPlayer,
        setIsOpen,
        streamEndReached,
        setIsEmbedded,
        isEmbedded: isEmbeddedPlayerContext,
        asset,
    } = usePlayer();
    const { castConnected, mediaInfo } = useCast();
    const { params, playerType } = usePlayerRouter();
    const { location } = useNavigation();
    const { config } = useConfig();
    const [props, setProps] = useState<PlayerProps>();

    const actualProps: PlayerProps = useMemo(() => {
        if (inputProps) {
            return inputProps;
        }
        if (props) {
            return props;
        }
        return miniPlayerProps;
    }, [inputProps, props, miniPlayerProps]);

    useEffect(() => {
        const closePlayer = (location.state as { closePlayer?: boolean } | null)?.closePlayer;
        const miniPlayerEnabled =
            config.isFeatureActive('mini_player') && !castConnected && !mediaInfo?.loaded && !isMobile && !streamEndReached && !closePlayer;

        if (params && playerType) {
            setProps({
                params,
                type: playerType,
            });
        } else if (miniPlayerEnabled && props != null && asset) {
            switchToMiniPlayer(props);
        }

        if ((!miniPlayerEnabled || !asset) && !playerType) {
            setProps(null);
        }
    }, [location.pathname, params, playerType, streamEndReached, isEmbeddedPlayerContext]);

    useEffect(() => {
        if (miniPlayerProps) {
            setProps(null);
        }
    }, [miniPlayerProps]);

    useEffect(() => {
        if (playerType) {
            closeMiniPlayer();
        }
    }, [playerType]);

    useEffect(() => {
        setIsOpen(miniPlayerProps != null || props != null);
    }, [miniPlayerProps, props]);

    useEffect(() => {
        if (isEmbedded) {
            setIsEmbedded(true);
        }
    }, [isEmbedded]);

    useEffect(() => {
        if (castConnected && mediaInfo?.loaded) {
            closeMiniPlayer();
        }
    }, [castConnected, mediaInfo, miniPlayerProps]);

    const type = actualProps?.type;
    const playerParams = actualProps?.params;

    if (!type || !playerParams || !actualProps) return null;

    switch (type) {
        case PlayingAssetType.LIVE:
            return <LivePlayer channelId={playerParams.channelId} isEmbedded={isEmbedded} />;
        case PlayingAssetType.CATCHUP:
            return <CatchupPlayer broadcastId={decodeId(playerParams.broadcastId ?? '')} isEmbedded={isEmbedded} />;
        case PlayingAssetType.RECORDING:
            return <RecordingPlayer recordingId={decodeId(playerParams.recordingId ?? '')} />;
        case PlayingAssetType.VOD:
            return <VodPlayer assetId={decodeId(playerParams.assetId ?? '')} />;
        case PlayingAssetType.TRAILER:
            return <TrailerPlayer assetId={decodeId(playerParams.assetId ?? '')} />;
        default:
            return null;
    }
};
